import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Card, Table, Alert } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import ModalDatosFacturacion from '../rips/ModalDatosFacturacion';

function CompletarDatosFactura({ show, facturaId, onComplete, onCancel }) {
    const [factura, setFactura] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Estados para el flujo de modales
    const [showModalDatosFacturacion, setShowModalDatosFacturacion] = useState(false);
    const [showModalEnvioDian, setShowModalEnvioDian] = useState(false);
    const [showModalEnviando, setShowModalEnviando] = useState(false);
    const [mensajeEnvio, setMensajeEnvio] = useState('');
    const [errorEnvio, setErrorEnvio] = useState(null);
    
    const [datosRips, setDatosRips] = useState(null);
    
    // Estados para presupuesto activo
    const [presupuestosActivos, setPresupuestosActivos] = useState([]);
    const [presupuestoSeleccionado, setPresupuestoSeleccionado] = useState(null);
    const [saldoDisponible, setSaldoDisponible] = useState(0);

    useEffect(() => {
        if (show && facturaId) {
            cargarFactura();
        }
    }, [show, facturaId]);

    useEffect(() => {
        if (!show) {
            setFactura(null);
            setDatosRips(null);
            setShowModalDatosFacturacion(false);
            setShowModalEnvioDian(false);
            setShowModalEnviando(false);
            setError(null);
            setErrorEnvio(null);
            setCufeEnviado(null);
            setMensajeEnvio('');
            setRipsIdAsociado(null);
            setPresupuestosActivos([]);
            setPresupuestoSeleccionado(null);
            setSaldoDisponible(0);
        }
    }, [show]);

    const cargarFactura = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const response = await axios.get(`/factura/${facturaId}?uid=${userdata.user_id}`);
            
            if (response.status === 200) {
                setFactura(response.data);

                // RIPS ligado al borrador (para abrir modal SISPRO tras DIAN)
                try {
                    const ncRes = await axios.get(`/nota-credito/por-factura/${facturaId}`);
                    const ripsId = ncRes.data?.rips?.items?.[0]?.id || null;
                    setRipsIdAsociado(ripsId);
                } catch (e) {
                    setRipsIdAsociado(null);
                }

                // Cargar presupuestos activos del paciente (mismo endpoint que CrearFactura.js)
                const pacienteId = response.data.paciente_id;
                if (pacienteId) {
                    try {
                        const presupuestosResp = await axios.get(`/presupuestos/paciente/${pacienteId}/activos?uid=${userdata.user_id}`);
                        if (presupuestosResp.status === 200) {
                            const activos = presupuestosResp.data.presupuestos || [];
                            setPresupuestosActivos(activos);
                            console.log('Presupuestos activos cargados para RIPS:', activos.length);
                        }
                    } catch (e) {
                        console.log('No se pudieron cargar presupuestos activos:', e);
                        setPresupuestosActivos([]);
                    }
                }

                // La API devuelve `detalle` (join lista_precios), no `items`
                const detalle = response.data.detalle || [];
                const row0 = detalle[0];
                const row1 = detalle[1];
                const codigo0 = row0?.lista_precio?.codigorips || row0?.codigorips || '';
                const codigo1 = row1?.lista_precio?.codigorips || row1?.codigorips || '';

                // Calcular totales de ajuste
                const ajuste0 = parseFloat(row0?.ajuste ?? 0);
                const ajuste1 = parseFloat(row1?.ajuste ?? 0);
                const totalAjuste = ajuste0 + ajuste1;

                // Totales desde líneas (el encabezado del borrador RIPS a menudo queda desfasado)
                const totalBrutoLineas = detalle.reduce((sum, row) => {
                    const precio = parseFloat(row?.precio_unitario ?? 0) || 0;
                    const cant = parseFloat(row?.cantidad ?? 1) || 1;
                    return sum + precio * cant;
                }, 0);
                const totalPagarLineas = detalle.reduce(
                    (sum, row) => sum + (parseFloat(row?.total ?? 0) || 0),
                    0
                );

                setDatosRips({
                    tipo_consulta: !!row0,
                    cupsc: codigo0,
                    valorconsulta: String(row0?.precio_unitario ?? '0'),
                    descuento_consulta: String(row0?.porcentaje ?? '0'),
                    ajuste_consulta: String(ajuste0),
                    impuesto_consulta: String(row0?.impuesto ?? '0'),
                    total_consulta: String(row0?.total ?? '0'),

                    tipo_procedimiento: !!row1,
                    cupsp: codigo1,
                    valorprocedimiento: String(row1?.precio_unitario ?? '0'),
                    descuento_procedimiento: String(row1?.porcentaje ?? '0'),
                    ajuste_procedimiento: String(ajuste1),
                    impuesto_procedimiento: String(row1?.impuesto ?? '0'),
                    total_procedimiento: String(row1?.total ?? '0'),

                    total_bruto: String(totalBrutoLineas || response.data.valor_bruto || '0'),
                    total_ajuste: String(totalAjuste),
                    total_pagar: String(totalPagarLineas || response.data.valor_total || '0')
                });

                setShowModalDatosFacturacion(true);
            }
        } catch (err) {
            console.error('Error cargando factura:', err);
            setError('Error al cargar los datos de la factura');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSeleccionPresupuesto = (e) => {
        const selectedId = e.target.value;
        if (!selectedId) {
            setPresupuestoSeleccionado(null);
            setSaldoDisponible(0);
            return;
        }
        const selected = presupuestosActivos.find(p => String(p.id) === String(selectedId));
        if (selected) {
            setPresupuestoSeleccionado(selected);
            setSaldoDisponible(parseFloat(selected.saldo_disponible) || 0);
        }
    };

    const handleConfirmarDatosFacturacion = async (datosCompletos) => {
        const totalFactura = parseFloat(datosCompletos.total_pagar) || 0;
        if (presupuestoSeleccionado && totalFactura > saldoDisponible + 0.009) {
            alert(
                `El valor de la factura ($${totalFactura.toLocaleString('es-CO', { minimumFractionDigits: 2 })}) ` +
                `excede el saldo disponible del presupuesto ($${saldoDisponible.toLocaleString('es-CO', { minimumFractionDigits: 2 })})`
            );
            return;
        }

        try {
            setShowModalDatosFacturacion(false);
            setError(null);

            const parametrosfevId = parseInt(datosCompletos.prefijo, 10);
            if (!parametrosfevId) {
                setError('Debe seleccionar un prefijo / resolución FEV.');
                setShowModalDatosFacturacion(true);
                return;
            }

            const hoy = new Date().toISOString().split('T')[0];

            // Backend FacturaController::completarDatosFactura espera parametrosfev_id y forma_pago (código medio de pago DIAN, ej. 10)
            const datosActualizacion = {
                parametrosfev_id: parametrosfevId,
                forma_pago: String(datosCompletos.metodo_pago || '10'),
                tipo_pago: 'contado',
                valor_bruto: parseFloat(datosCompletos.total_bruto) || 0,
                valor_total: parseFloat(datosCompletos.total_pagar) || 0,
                fecha_emision: hoy,
                fecha_vence: hoy,
                presupuesto_id: presupuestoSeleccionado ? parseInt(presupuestoSeleccionado.id) : null,
                contract_number: datosCompletos.contract_number || null,
                policy_number: datosCompletos.policy_number || null,
                ajuste_consulta: parseFloat(datosCompletos.ajuste_consulta) || 0,
                ajuste_procedimiento: parseFloat(datosCompletos.ajuste_procedimiento) || 0,
                total_consulta: parseFloat(datosCompletos.total_consulta) || 0,
                total_procedimiento: parseFloat(datosCompletos.total_procedimiento) || 0,
                descuento_consulta: parseFloat(datosCompletos.descuento_consulta) || 0,
                descuento_procedimiento: parseFloat(datosCompletos.descuento_procedimiento) || 0,
                impuesto_consulta: parseFloat(datosCompletos.impuesto_consulta) || 0,
                impuesto_procedimiento: parseFloat(datosCompletos.impuesto_procedimiento) || 0,
                valorconsulta: parseFloat(datosCompletos.valorconsulta) || 0,
                valorprocedimiento: parseFloat(datosCompletos.valorprocedimiento) || 0,
            };

            const response = await axios.put(`/facturas/${facturaId}/completar-datos`, datosActualizacion);

            if (response.status === 200 && response.data?.success !== false) {
                // Enviar directamente a la DIAN sin preguntar (borrador RIPS ya completó datos)
                enviarDirectoADian();
            } else {
                throw new Error(response.data?.message || 'Respuesta inesperada del servidor');
            }
        } catch (err) {
            console.error('Error completando datos:', err);
            let msg =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Error al completar los datos de facturación';
            const ve = err.response?.data?.errors;
            if (ve && typeof ve === 'object') {
                msg = Object.values(ve).flat().join(' ') || msg;
            }
            setError(msg);
            alert(msg);
            setShowModalDatosFacturacion(true);
        }
    };
    
    // Estado para guardar el CUFE
    const [cufeEnviado, setCufeEnviado] = useState(null);
    const [ripsIdAsociado, setRipsIdAsociado] = useState(null);

    // Función para enviar directamente a DIAN (sin modal de confirmación)
    const enviarDirectoADian = async () => {
        setShowModalEnviando(true);
        setMensajeEnvio('Enviando factura a la DIAN...');
        setErrorEnvio(null);
        setCufeEnviado(null);
        
        try {
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const response = await axios.post(`/factura/${facturaId}/enviar-dian?uid=${userdata.user_id}`, {});
            
            if (response.data.success) {
                setMensajeEnvio('Factura enviada exitosamente a la DIAN');
                setCufeEnviado(response.data.uuid || null);
                setErrorEnvio(null);
            } else {
                setMensajeEnvio('Error al enviar a la DIAN');
                setErrorEnvio({
                    message: response.data.message || 'Error desconocido',
                    response: response.data.response,
                    json_enviado: response.data.json_enviado,
                    status_code: response.data.status_code
                });
            }
        } catch (envioError) {
            console.error('Error enviando a DIAN:', envioError);
            setMensajeEnvio('Error al enviar a la DIAN');
            setErrorEnvio({
                message: envioError.response?.data?.message || envioError.message,
                status: envioError.response?.status,
                statusText: envioError.response?.statusText,
                data: envioError.response?.data,
                fullError: envioError.response || envioError
            });
        }
    };

    const handleConfirmarEnvioDian = async () => {
        setShowModalEnvioDian(false);
        enviarDirectoADian();
    };
    
    const handleCancelarEnvioDian = () => {
        setShowModalEnvioDian(false);
        onComplete({ facturaId, dianOk: false, ripsId: ripsIdAsociado });
    };
    
    const notificarCierre = (abrirRips) => {
        const envioOk = !errorEnvio && !String(mensajeEnvio || '').includes('Enviando');
        setShowModalEnviando(false);
        onComplete({
            facturaId,
            dianOk: envioOk,
            abrirRips: !!abrirRips && envioOk,
            ripsId: ripsIdAsociado,
            factura: factura
                ? {
                    id: factura.id,
                    paciente_id: factura.paciente_id,
                    users_id: factura.users_id,
                    fecha_emision: factura.fecha_emision,
                    prefijo: factura.prefijo,
                    numero_factura: factura.numero_factura,
                    estado: factura.estado,
                    tipo_factura: factura.tipo_factura,
                }
                : null,
        });
    };

    const handleCerrarModalEnviando = () => {
        notificarCierre(false);
    };

    const handleContinuarConRips = () => {
        notificarCierre(true);
    };

    const formatearValor = (valor) => {
        return '$' + Math.round(parseFloat(valor || 0)).toLocaleString('es-CO');
    };

    if (loading) {
        return (
            <Modal show={show} onHide={onCancel} size="lg" centered>
                <Modal.Body className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                    <p className="mt-3">Cargando datos de la factura...</p>
                </Modal.Body>
            </Modal>
        );
    }

    if (show && error && !factura) {
        return (
            <Modal show={show} onHide={onCancel} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger" className="mb-0">{error}</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onCancel}>Cerrar</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <>
            {/* Modal de Datos de Facturación */}
            {factura && datosRips && (
                <ModalDatosFacturacion
                    show={showModalDatosFacturacion}
                    onClose={() => {
                        setShowModalDatosFacturacion(false);
                        onCancel();
                    }}
                    onConfirm={handleConfirmarDatosFacturacion}
                    profesionalId={factura.users_id}
                    datosRips={datosRips}
                    filtroPrefijoRelajado={true}
                    parametrosfevIdGuardado={factura.parametrosfev_id}
                    presupuestosActivos={presupuestosActivos}
                    presupuestoSeleccionado={presupuestoSeleccionado}
                    onSeleccionPresupuesto={handleSeleccionPresupuesto}
                    saldoDisponible={saldoDisponible}
                />
            )}
            
            {/* Modal de Confirmación de Envío a DIAN */}
            <Modal show={showModalEnvioDian} onHide={handleCancelarEnvioDian} centered>
                <Modal.Header closeButton className="border-0" style={{ background: '#f8f9fa' }}>
                    <Modal.Title className="text-dark font-weight-bold h6 mb-0">
                        <i className="fas fa-file-invoice mr-2 text-info"></i>
                        Factura Completada
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-file-invoice text-secondary" style={{ fontSize: '64px', opacity: 0.9 }}></i>
                    </div>
                    <h5 className="mb-3">¡Datos de facturación completados exitosamente!</h5>
                    <Alert variant="info" className="text-left">
                        <strong>Factura ID:</strong> {facturaId}<br />
                        <strong>Estado:</strong> Generada (lista para enviar a DIAN)
                    </Alert>
                    <p className="mb-0">¿Desea enviar esta factura a la DIAN ahora?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelarEnvioDian}>
                        <i className="fas fa-clock mr-1"></i>
                        No, enviar después
                    </Button>
                    <Button variant="primary" onClick={handleConfirmarEnvioDian}>
                        <i className="fas fa-paper-plane mr-1"></i>
                        Sí, enviar ahora
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Modal de Envío en Progreso / Resultado */}
            <Modal 
                show={showModalEnviando} 
                onHide={handleCerrarModalEnviando} 
                centered
                backdrop="static"
            >
                <Modal.Header
                    closeButton
                    className={`border-0 ${errorEnvio ? 'border-bottom border-danger' : ''}`}
                    style={{
                        background: '#f8f9fa',
                        ...(errorEnvio ? { borderBottomWidth: '3px', borderBottomStyle: 'solid' } : {})
                    }}
                >
                    <Modal.Title className="text-dark font-weight-bold h6 mb-0">
                        {mensajeEnvio.includes('Enviando') && !errorEnvio ? (
                            <><i className="fas fa-paper-plane mr-2 text-info"></i>Enviando a la DIAN</>
                        ) : errorEnvio ? (
                            <><i className="fas fa-exclamation-triangle mr-2 text-danger"></i>Error al Enviar a la DIAN</>
                        ) : (
                            <><i className="fas fa-check-circle mr-2 text-info"></i>Enviado Exitosamente</>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    {/* Icono dinámico según el estado */}
                    {mensajeEnvio.includes('Enviando') && !errorEnvio ? (
                        <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                            <span className="sr-only">Enviando...</span>
                        </div>
                    ) : errorEnvio ? (
                        <i className="fas fa-times-circle text-danger mb-3" style={{ fontSize: '64px' }}></i>
                    ) : (
                        <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: '64px' }}></i>
                    )}
                    
                    <h5 className="mb-3">{mensajeEnvio}</h5>
                    
                    {/* Mostrar CUFE de forma legible */}
                    {cufeEnviado && !errorEnvio && (
                        <div className="text-left mt-3">
                            <div className="alert alert-success">
                                <div className="d-flex align-items-center mb-2">
                                    <i className="fas fa-check-circle mr-2 text-success"></i>
                                    <strong>Factura enviada exitosamente a la DIAN</strong>
                                </div>
                                <div className="mt-2">
                                    <small className="text-muted d-block mb-1">CUFE:</small>
                                    <code style={{ 
                                        wordBreak: 'break-all', 
                                        display: 'block',
                                        backgroundColor: '#e9ecef',
                                        padding: '8px 12px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontFamily: 'monospace'
                                    }}>
                                        {cufeEnviado}
                                    </code>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {errorEnvio && (
                        <div className="text-left">
                            <Alert variant="danger">
                                <strong>Detalles del error:</strong>
                                <pre style={{ 
                                    maxHeight: '300px', 
                                    overflow: 'auto', 
                                    backgroundColor: '#f8f9fa',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    fontSize: '12px'
                                }}>
                                    {JSON.stringify(errorEnvio, null, 2)}
                                </pre>
                            </Alert>
                            <Alert variant="info">
                                <strong>Sugerencias:</strong>
                                <ul className="mb-0 mt-2">
                                    <li>Verifique que los parámetros de facturación electrónica estén configurados correctamente</li>
                                    <li>Revise que el prefijo seleccionado esté habilitado</li>
                                    <li>Verifique la conexión con el servidor de la DIAN</li>
                                    <li>Puede reintentar el envío desde el listado de facturas</li>
                                </ul>
                            </Alert>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!errorEnvio && !mensajeEnvio.includes('Enviando') ? (
                        <>
                            <Button variant="secondary" onClick={handleCerrarModalEnviando}>
                                Cerrar
                            </Button>
                            <Button variant="dark" onClick={handleContinuarConRips}>
                                <i className="fas fa-notes-medical mr-1"></i>
                                Enviar RIPS
                            </Button>
                        </>
                    ) : (
                        <Button variant="primary" onClick={handleCerrarModalEnviando} disabled={mensajeEnvio.includes('Enviando') && !errorEnvio}>
                            <i className="fas fa-check mr-1"></i>
                            Cerrar
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default CompletarDatosFactura;
